import React, { useState, useRef, useEffect } from 'react';

const MultiSelectCategory = ({ categories = [], value = [], onChange, isLoading = false, error, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleOption = (categoryId) => {
    const newValue = value.includes(categoryId)
      ? value.filter(v => v !== categoryId)
      : [...value, categoryId];
    onChange(newValue);
  };

  const handleRemoveOption = (categoryId, e) => {
    e.stopPropagation();
    const newValue = value.filter(v => v !== categoryId);
    onChange(newValue);
  };

  const getSelectedLabels = () => {
    return value.map(id => {
      const category = categories.find(c => c.id === id);
      return category ? category.name : id;
    });
  };

  const selectedLabels = getSelectedLabels();

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`min-h-[42px] w-full rounded-lg border px-3 py-2 cursor-pointer focus:outline-none focus:ring-1 ${
          error 
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
            : 'border-gray-200 focus:border-sky-500 focus:ring-sky-500'
        }`}
        onClick={() => setIsOpen(!isOpen)}
        role="listbox"
        tabIndex={0}
        aria-label="Select categories"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
      >
        {selectedLabels.length === 0 ? (
          <span className="text-gray-400">
            {isLoading ? 'Loading categories...' : '-- Select categories --'}
          </span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {selectedLabels.map((label, index) => {
              const categoryId = value[index];
              return (
                <span
                  key={categoryId}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800"
                >
                  {label}
                  <button
                    type="button"
                    onClick={(e) => handleRemoveOption(categoryId, e)}
                    className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-sky-200 focus:outline-none"
                    aria-label={`Remove ${label}`}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              );
            })}
          </div>
        )}

        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {categories.length === 0 ? (
            <div className="px-3 py-2 text-gray-400 text-sm">No categories available</div>
          ) : (
            categories.map((category) => (
              <div
                key={category.id}
                className={`px-3 py-2 cursor-pointer hover:bg-gray-50 flex items-center ${
                  value.includes(category.id) ? 'bg-sky-50' : ''
                }`}
                onClick={() => handleToggleOption(category.id)}
                role="option"
                aria-selected={value.includes(category.id)}
              >
                <input
                  type="checkbox"
                  checked={value.includes(category.id)}
                  onChange={() => {}}
                  className="mr-2 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                  tabIndex={-1}
                />
                <span className={value.includes(category.id) ? 'font-medium text-sky-900' : 'text-gray-900'}>
                  {category.name}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectCategory;
